import { hideIrrelevantMentions } from "@/api/endpoints/searches";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@components/ui/alert";
import { EyeOffIcon, InfoIcon } from "lucide-react";
import { Button } from "@components/ui/button";
import { Trans, useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { Spinner } from "@components/ui/spinner";
import { useSearches } from "@/hooks/useSearches";

export default function SmartRelevance({
  llmIrrelevantMentionsCount,
  searchUuid,
  orgUuid,
}: {
  llmIrrelevantMentionsCount: number;
  searchUuid: Uuid;
  orgUuid: Uuid;
}) {
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();

  const { data: searchesData } = useSearches(orgUuid);

  const { mutate: mutateHideIrrelevantMentions, isPending } = useMutation({
    mutationFn: () => hideIrrelevantMentions(searchUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [orgUuid, "searches", searchUuid, "mention-cards"],
      });
    },
  });

  const search = searchesData?.ok
    ? searchesData.data.find((s) => s.uuid === searchUuid)
    : undefined;

  // Helpers
  const { shouldRender, isVisible } = useDelayedLoading(isPending);

  return (
    <Alert variant="blue">
      <InfoIcon />
      <AlertTitle>
        <Trans>
          {llmIrrelevantMentionsCount || t`Inga`}{" "}
          {plural(llmIrrelevantMentionsCount, {
            one: "träff",
            other: "träffar",
          })}{" "}
          med låg relevans
        </Trans>
      </AlertTitle>
      <AlertDescription>
        <Trans>Bedömt utifrån frågan</Trans>: &quot;
        {search?.information_need}
        &quot;
      </AlertDescription>
      <AlertAction>
        <Button
          size="sm"
          variant="outline"
          disabled={shouldRender}
          onClick={() => {
            mutateHideIrrelevantMentions();
          }}
        >
          {isVisible ? <Spinner /> : <EyeOffIcon />}
          <Trans>Dölj alla</Trans>
        </Button>
      </AlertAction>
    </Alert>
  );
}
